import axios from 'axios';
import { uploadBase64Image } from './storage';

interface ImageDetail {
  type: 'Street View' | 'Zillow' | 'Web';
  url: string;
  number?: number;
}

export async function analyzeImages(
  images: string[],
  hasZillowImages: boolean = false,
  imageDetails: ImageDetail[] = [],
  address?: string,
  zillowMainURL?: string
): Promise<{ url: string; reason: string }> {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyzeImages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        images,
        hasZillowImages,
        imageDetails,
        address,
        zillowMainURL
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to analyze images');
    }

    const data = await response.json();

    const { url: selectedUrl, reason } = data;
    
    if (!selectedUrl) {
      throw new Error('No suitable image was found');
    }

    // Verify the URL exists in our list
    if (!images.includes(selectedUrl)) {
      console.error('Selected URL not found in original images:', {
        selectedUrl,
        availableImages: images
      });
      throw new Error('Selected image URL does not match any available images');
    }

    return { url: selectedUrl, reason };
  } catch (error) {
    console.error('Error analyzing images:', error);
    throw error;
  }
}

/**
 * Process and resize an image from a URL to meet OpenAI's requirements
 */
export async function processImageForOpenAI(imageUrl: string): Promise<string> {
  try {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      
      img.onload = () => {
        try {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          // Determine which dimension to scale based on
          if (width > height) {
            if (height > 768) {
              width = (width * 768) / height;
              height = 768;
            }
            if (width > 2000) {
              height = (height * 2000) / width;
              width = 2000;
            }
          } else {
            if (width > 768) {
              height = (height * 768) / width;
              width = 768;
            }
            if (height > 2000) {
              width = (width * 2000) / height;
              height = 2000;
            }
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(width);
          canvas.height = Math.round(height);
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          
          // Use better image smoothing
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw image with white background
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert to base64
          const base64Data = canvas.toDataURL('image/jpeg', 0.95);
          resolve(base64Data);
        } catch (error) {
          console.error('Error processing image in canvas:', error);
          reject(new Error(`Failed to process image: ${error.message}`));
        }
      };
      
      img.onerror = (error) => {
        console.error('Error loading image:', error);
        reject(new Error('Failed to load image'));
      };
      
      // Add cache buster to avoid CORS issues
      img.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
    });
  } catch (error) {
    console.error('Error in processImageForOpenAI:', error);
    throw error;
  }
}

/**
 * Fetches an image from a URL and converts it to a File object
 */
export async function urlToFile(url: string, filename: string = 'image.jpg', mimeType: string = 'image/jpeg'): Promise<File> {
  try {
    const response = await fetch(url, { 
      mode: 'cors',
      headers: {
        'Accept': 'image/*',
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    return new File([blob], filename, { type: mimeType });
  } catch (error) {
    console.error('Error converting URL to file:', error);
    throw error;
  }
}

/**
 * Processes an image to make it square by adding padding
 */
export async function makeImageSquare(imageUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    
    img.onload = () => {
      // Calculate dimensions of the square canvas
      const size = Math.max(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        return reject(new Error('Failed to create canvas context'));
      }
      
      // Fill the canvas with a white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      
      // Calculate offsets to center the image
      const offsetX = (size - img.width) / 2;
      const offsetY = (size - img.height) / 2;
      
      // Draw the image centered in the canvas
      ctx.drawImage(img, offsetX, offsetY, img.width, img.height);
      
      // Convert to base64
      const base64Data = canvas.toDataURL('image/png');
      resolve(base64Data);
    };
    
    img.onerror = (err) => {
      console.error('Image loading error:', err);
      reject(new Error('Failed to load image'));
    };
    
    // Add a timestamp/cache buster to avoid CORS issues
    img.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}cachebuster=${Date.now()}`;
  });
}

/**
 * Uploads OpenAI generated image to Supabase and returns a URL
 */
export async function uploadOpenAIGeneratedImage(base64ImageData: string, prefix: string = 'ai'): Promise<string> {
  try {
    // Validate base64 data
    if (!base64ImageData || !base64ImageData.startsWith('data:image')) {
      throw new Error('Invalid base64 image data provided');
    }

    // Generate a unique filename with the prefix
    const filename = `${prefix}-${Date.now()}.jpg`;
    
    // Upload to Supabase storage
    const publicUrl = await uploadBase64Image(base64ImageData, 'propertyimages', filename);
    
    return publicUrl;
  } catch (error: any) {
    console.error(`Error uploading ${prefix} image to Supabase:`, error);
    
    // Provide more specific error information
    if (error.message && error.message.includes('Unexpected token')) {
      throw new Error(`Storage server returned HTML instead of JSON - this is a temporary server issue. Please try again.`);
    } else if (error.status === 502) {
      throw new Error(`Storage server is temporarily unavailable (502 Bad Gateway). Please try again.`);
    } else if (error.status === 503) {
      throw new Error(`Storage service is temporarily unavailable (503). Please try again.`);
    } else if (error.status === 504) {
      throw new Error(`Storage request timed out (504). Please try again.`);
    } else {
      throw new Error(`Failed to upload image: ${error.message || 'Unknown error'}`);
    }
  }
}

/**
 * Calls the Supabase function to edit an image using Primary OpenAI (Azure)
 */
export async function editImageWithOpenAI(imageData: string, prompt: string, quality: string = 'low'): Promise<string> {
  try {
    if (!imageData) {
      throw new Error('No image data provided');
    }

    if (!prompt) {
      throw new Error('No prompt provided');
    }

    // Validate the image data is a proper base64 string
    if (!imageData.startsWith('data:image')) {
      throw new Error('Invalid image data format');
    }

    // Use Primary OpenAI (Azure) directly
    const maxRetries = 3;
    let lastError = null;
    
    // Generate a unique request ID for retry tracking
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🎨 Attempt ${attempt}/${maxRetries} - Using Primary OpenAI (Azure) for image generation`);
        
        const response = await axios.post(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/primary-openai-image`,
          {
            prompt,
            imageData,
            quality,
            request_id: requestId // Include request ID for retry tracking
          },
          {
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json'
            },
            timeout: 180000 // 180 second timeout (3 minutes)
          }
        );

        if (response.data?.url) {
          // If the response is a base64 data URL, upload it to Supabase storage
          if (response.data.url.startsWith('data:image')) {
            // Determine the style prefix based on the prompt
            const prefix = prompt.toLowerCase().includes('pencil') ? 'pencil' :
                        prompt.toLowerCase().includes('oil') ? 'oil' :
                        prompt.toLowerCase().includes('watercolor') ? 'watercolor' :
                        prompt.toLowerCase().includes('charcoal') ? 'charcoal' :
                        prompt.toLowerCase().includes('chalk') ? 'chalk' : 'ai';
            
            // Upload to Supabase storage and get public URL
            return await uploadOpenAIGeneratedImage(response.data.url, prefix);
          }
          // Otherwise return the URL directly
          return response.data.url;
        } else {
          throw new Error(`Invalid response from Primary OpenAI (Azure) Supabase function: Missing URL`);
        }
      } catch (error) {
        console.error(`❌ Primary OpenAI (Azure) attempt ${attempt} failed:`, error);
        lastError = error;

        // Log the failure reason for monitoring
        if (axios.isAxiosError(error) && error.response) {
          console.error(`Primary OpenAI (Azure) API error details:`, error.response.data);
          
          // Check if this is a rate limit error (429 status)
          if (error.response.status === 429) {
            // For rate limit errors, provide user-friendly message
            const rateLimitMessage = error.response.data?.message || 'Our servers are busy right now. Please try again in a few minutes.';
            
            // If this is the last attempt, throw a user-friendly error
            if (attempt === maxRetries) {
              throw new Error(rateLimitMessage);
            }
          }
        }

        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
          console.log(`⏳ Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // If we get here, all retries failed
    const errorMessage = axios.isAxiosError(lastError) && lastError.response?.data?.error
      ? lastError.response.data.error
      : lastError?.message || 'Unknown error';

    throw new Error(`Failed to generate image after ${maxRetries} attempts using Primary OpenAI (Azure): ${errorMessage}`);
  } catch (error) {
    console.error('Error calling Primary OpenAI (Azure) image generation API:', error);
    
    if (axios.isAxiosError(error) && error.response) {
      console.error('Final API error details:', error.response.data);
    }
    
    throw error;
  }
}